"use client";

import { useEffect, useRef, useState } from "react";
import { Check, Copy, Wallet } from "lucide-react";
import { announce } from "@/lib/a11y/announce";

/** The artist's wallet as a way to reach them, beside follow and message.
 *  Both forms of the address are rendered; a container query shows the full
 *  one whenever the strip is wide enough, the shortened one otherwise. The
 *  button copies the full address either way and announces it. */
export default function WalletStrip({ address, lang }: { address: string; lang: "fa" | "en" }) {
  const isFa = lang === "fa";
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const short = `${address.slice(0, 6)}…${address.slice(-4)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      announce(isFa ? "نشانی کیف پول کپی شد" : "Wallet address copied", "polite");
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // Refused clipboard: the address is still on screen and selectable.
    }
  };

  return (
    <div className="ph-wallet" data-copied={copied || undefined}>
      <span className="ph-wallet-mark" aria-hidden><Wallet /></span>
      <span className="ph-wallet-body">
        <span className="ph-wallet-label">{isFa ? "کیف پول" : "Wallet"}</span>
        <span className="ph-wallet-addr" title={address}>
          <span className="ph-wallet-full">{address}</span>
          <span className="ph-wallet-short">{short}</span>
        </span>
      </span>
      <button type="button" className="ph-wallet-copy v2-focus" onClick={copy} aria-label={`${isFa ? "کپی نشانی کیف پول" : "Copy wallet address"}: ${address}`}>
        {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
        <span className="ph-wallet-copy-text">{copied ? (isFa ? "کپی شد" : "Copied") : (isFa ? "کپی" : "Copy")}</span>
      </button>
    </div>
  );
}
